// Generic, reusable people-search picker for the platform: a debounced global
// people search (identity.discover gated, server-side) rendered as an icon input
// field with a floating results dropdown (avatar + name + email + add). Callers
// supply their own labels and a per-result `onSelect`, and may add an optional
// `trailing` control (e.g. a role dropdown) and/or an `onSubmitRaw` handler (e.g.
// invite a typed email that isn't in the results). Use this anywhere the platform
// needs a "find a person" affordance so the UX stays consistent.
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { searchPeople, PeopleSearchResult } from '../../../api/marketplaceApi';
import SpinnerAtom from '../../atoms/Spinner/SpinnerAtom';
import './PeopleSearchMolecule.scss';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;
const DEFAULT_MAX_RESULTS = 6;

/** Caller-supplied, already-localized strings. */
export interface PeopleSearchLabels {
  searchPlaceholder: string;
  /** Per-row add affordance (e.g. "+ Add" / "+ Invite"). */
  addButton: string;
  /** Receives the trimmed query for interpolation. */
  noMatch: (query: string) => string;
  /** Shown when the typed query exactly matches an already-excluded email. Omit to disable. */
  alreadyAdded?: string;
  /** Submit-button label when `onSubmitRaw` is provided (e.g. "Send invite"). */
  sendButton?: string;
  /** Submit-button in-progress label. */
  sending?: string;
}

export interface PeopleSearchMoleculeProps {
  /** Called when a searched person is chosen. Resolve true to clear + close. */
  onSelect: (person: PeopleSearchResult) => Promise<boolean>;
  labels: PeopleSearchLabels;
  /** Disables interaction while a parent mutation is in flight. */
  isMutating?: boolean;
  /** Identity IDs to drop from results (already added). */
  excludeIdentityIds?: string[];
  /** Lowercased emails to drop from results and to drive the "already added" note. */
  excludeEmails?: string[];
  /** Optional control rendered after the field (e.g. a role dropdown). */
  trailing?: React.ReactNode;
  /** When set, renders a submit button that adds the raw typed query (e.g. an email
   *  not in the results). Resolve true to clear the field. */
  onSubmitRaw?: (raw: string) => Promise<boolean>;
  /** PrimeIcon class for the field icon (default 'pi-search'). */
  inputIcon?: string;
  /** Max rows shown after exclusion filtering (default 6). */
  maxResults?: number;
  /** Fired after each debounced search settles (for analytics). */
  onSearched?: (resultCount: number) => void;
  className?: string;
}

const PeopleSearchMolecule: React.FC<PeopleSearchMoleculeProps> = ({
  onSelect,
  labels,
  isMutating = false,
  excludeIdentityIds,
  excludeEmails,
  trailing,
  onSubmitRaw,
  inputIcon = 'pi-search',
  maxResults = DEFAULT_MAX_RESULTS,
  onSearched,
  className = '',
}) => {
  const apiClients = useApiClientsOrNull();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<PeopleSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const idSet = useMemo(() => new Set(excludeIdentityIds ?? []), [excludeIdentityIds]);
  const emailSet = useMemo(
    () => new Set((excludeEmails ?? []).map((e) => e.toLowerCase())),
    [excludeEmails]
  );
  // Over-fetch by the exclusion count so filtering still leaves up to maxResults rows.
  const overFetch = Math.max(10, maxResults + idSet.size + emailSet.size);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Debounced global people search.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < MIN_QUERY_LEN) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      if (!apiClients?.searchClient) return;
      setSearchLoading(true);
      try {
        const res = await searchPeople(apiClients.searchClient, q, overFetch, 0);
        const filtered = res.results
          .filter((r) => !idSet.has(r.identityId) && !emailSet.has((r.email ?? '').toLowerCase()))
          .slice(0, maxResults);
        setResults(filtered);
        setOpen(true);
        onSearched?.(filtered.length);
      } catch {
        setResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, apiClients, overFetch, idSet, emailSet, maxResults, onSearched]);

  const reset = useCallback(() => {
    setQuery('');
    setResults([]);
    setOpen(false);
  }, []);

  const handleSelect = useCallback(
    async (person: PeopleSearchResult) => {
      const ok = await onSelect(person);
      if (ok) reset();
    },
    [onSelect, reset]
  );

  const handleSubmitRaw = useCallback(async () => {
    if (!onSubmitRaw) return;
    const raw = query.trim();
    if (!raw) return;
    setSubmitting(true);
    try {
      const ok = await onSubmitRaw(raw);
      if (ok) reset();
    } finally {
      setSubmitting(false);
    }
  }, [onSubmitRaw, query, reset]);

  const trimmed = query.trim().toLowerCase();
  const alreadyAdded = !!labels.alreadyAdded && trimmed.length >= MIN_QUERY_LEN && emailSet.has(trimmed);
  const busy = isMutating || submitting;

  return (
    <div ref={wrapperRef} className={['people-search-molecule', className].filter(Boolean).join(' ')}>
      <div className="people-search-molecule__row">
        <div className="people-search-molecule__field">
          <i className={`pi ${inputIcon} people-search-molecule__field-icon`} aria-hidden="true" />
          <input
            type="text"
            className="people-search-molecule__input"
            placeholder={labels.searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              if (query.trim().length >= MIN_QUERY_LEN) setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && onSubmitRaw) {
                e.preventDefault();
                void handleSubmitRaw();
              }
            }}
            disabled={busy}
            aria-label={labels.searchPlaceholder}
            autoComplete="off"
          />
          {searchLoading && <SpinnerAtom size="small" />}
        </div>

        {trailing}

        {onSubmitRaw && (
          <button
            type="button"
            className="people-search-molecule__send-btn"
            onClick={() => void handleSubmitRaw()}
            disabled={!query.trim() || busy}
          >
            <i className="pi pi-send" aria-hidden="true" />
            {submitting ? labels.sending ?? '' : labels.sendButton ?? ''}
          </button>
        )}
      </div>

      {open && trimmed.length >= MIN_QUERY_LEN && (
        <div className="people-search-molecule__dropdown" role="listbox">
          {results.length > 0 ? (
            results.map((person) => (
              <button
                key={person.identityId}
                type="button"
                role="option"
                className="people-search-molecule__result"
                onClick={() => void handleSelect(person)}
                disabled={isMutating}
              >
                <span className="people-search-molecule__result-avatar" aria-hidden="true">
                  {(person.displayName ?? person.email ?? '?').charAt(0).toUpperCase()}
                </span>
                <span className="people-search-molecule__result-info">
                  <span className="people-search-molecule__result-name">{person.displayName}</span>
                  <span className="people-search-molecule__result-email">{person.email}</span>
                </span>
                <span className="people-search-molecule__result-add">{labels.addButton}</span>
              </button>
            ))
          ) : !searchLoading ? (
            <p className="people-search-molecule__dropdown-empty">{labels.noMatch(query.trim())}</p>
          ) : null}
        </div>
      )}

      {alreadyAdded && <p className="people-search-molecule__already-added">{labels.alreadyAdded}</p>}
    </div>
  );
};

export default PeopleSearchMolecule;
